'use client';

import React, { useState, useCallback } from 'react';
import { X, Search, Send, Users, User, Check } from 'lucide-react';
import { Message } from './types';

interface Contact {
  id: string;
  name: string;
  initials: string;
  avatarGradient: string;
  isGroup?: boolean;
  isOnline?: boolean;
  subtitle: string;
}

const CONTACTS: Contact[] = [
  {
    id: '1',
    name: 'Ate Boni',
    initials: 'AB',
    avatarGradient: 'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)',
    isOnline: true,
    subtitle: 'Online',
  },
  {
    id: '2',
    name: 'Design Team',
    initials: 'DT',
    avatarGradient: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
    isGroup: true,
    subtitle: '5 members',
  },
  {
    id: '3',
    name: 'Marco Reyes',
    initials: 'MR',
    avatarGradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
    isOnline: true,
    subtitle: 'Online',
  },
  {
    id: '4',
    name: 'Jess & Friends',
    initials: 'JF',
    avatarGradient: 'linear-gradient(135deg, #EC4899 0%, #BE185D 100%)',
    isGroup: true,
    subtitle: '8 members',
  },
  {
    id: '5',
    name: 'Kuya Dan',
    initials: 'KD',
    avatarGradient: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
    subtitle: 'Last seen Mon',
  },
  {
    id: '6',
    name: 'Ana Santos',
    initials: 'AS',
    avatarGradient: 'linear-gradient(135deg, #06B6D4 0%, #0284C7 100%)',
    isOnline: true,
    subtitle: 'Online',
  },
  {
    id: '7',
    name: 'Work Updates',
    initials: 'WU',
    avatarGradient: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
    isGroup: true,
    subtitle: '12 members',
  },
  {
    id: '8',
    name: 'Lola Nena',
    initials: 'LN',
    avatarGradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    subtitle: 'Last seen Sat',
  },
  {
    id: '9',
    name: 'Barkada GC',
    initials: 'BG',
    avatarGradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
    isGroup: true,
    subtitle: '15 members',
  },
  {
    id: '10',
    name: 'Paolo Cruz',
    initials: 'PC',
    avatarGradient: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
    subtitle: 'Last seen Thu',
  },
];

interface ForwardShareModalProps {
  message: Message | null;
  mode: 'forward' | 'share';
  onClose: () => void;
}

export default function ForwardShareModal({ message, mode, onClose }: ForwardShareModalProps) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<'all' | 'contacts' | 'groups'>('all');
  const [sent, setSent] = useState(false);

  const filtered = CONTACTS.filter(c => {
    const matchesQuery = c.name.toLowerCase().includes(query.toLowerCase());
    if (filter === 'contacts') return matchesQuery && !c.isGroup;
    if (filter === 'groups') return matchesQuery && c.isGroup;
    return matchesQuery;
  });

  const toggleSelect = useCallback((id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSend = useCallback(() => {
    if (selected.size === 0) return;
    setSent(true);
    setTimeout(() => {
      onClose();
    }, 1200);
  }, [selected, onClose]);

  const previewText = message?.text
    ? message.text.length > 60
      ? message.text.slice(0, 60) + '…'
      : message.text
    : message?.imageUrl
    ? '📷 Photo' :'';

  const title = mode === 'forward' ? 'Forward Message' : 'Share Message';

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.65)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl flex flex-col"
        style={{
          background: '#1A2B3C',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.7)',
          maxHeight: '85dvh',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 flex-shrink-0">
          <div>
            <h2 className="text-white font-semibold text-[16px]">{title}</h2>
            {selected.size > 0 && (
              <p className="text-[12px] mt-0.5" style={{ color: '#4DA3FF' }}>
                {selected.size} recipient{selected.size > 1 ? 's' : ''} selected
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full transition-colors"
            style={{ background: 'rgba(255,255,255,0.07)' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.13)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.07)')}
            aria-label="Close"
          >
            <X size={16} className="text-white" />
          </button>
        </div>

        {/* Message preview */}
        {previewText && (
          <div
            className="mx-5 mb-3 px-3 py-2 rounded-xl flex-shrink-0"
            style={{ background: 'rgba(255,255,255,0.05)', borderLeft: '3px solid #4DA3FF' }}
          >
            <p className="text-[11px] font-medium mb-0.5" style={{ color: '#4DA3FF' }}>
              {mode === 'forward' ? 'Forwarding' : 'Sharing'}
            </p>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.75)' }}>{previewText}</p>
          </div>
        )}

        {/* Search */}
        <div className="px-5 mb-3 flex-shrink-0">
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-xl"
            style={{ background: 'rgba(255,255,255,0.06)' }}
          >
            <Search size={15} style={{ color: 'rgba(255,255,255,0.4)' }} />
            <input
              type="text"
              placeholder="Search contacts or groups…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="flex-1 bg-transparent text-white text-[13px] outline-none placeholder:text-white/30"
              autoFocus
            />
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 px-5 mb-3 flex-shrink-0">
          {(['all', 'contacts', 'groups'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium transition-all capitalize"
              style={{
                background: filter === tab ? '#287FD8' : 'rgba(255,255,255,0.07)',
                color: filter === tab ? '#fff' : 'rgba(255,255,255,0.55)',
              }}
            >
              {tab === 'contacts' && <User size={11} />}
              {tab === 'groups' && <Users size={11} />}
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Contact list */}
        <div className="flex-1 overflow-y-auto px-3 pb-2" style={{ minHeight: 0 }}>
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 gap-2">
              <Search size={28} style={{ color: 'rgba(255,255,255,0.2)' }} />
              <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.35)' }}>No results found</p>
            </div>
          ) : (
            filtered.map(contact => {
              const isSelected = selected.has(contact.id);
              return (
                <button
                  key={contact.id}
                  onClick={() => toggleSelect(contact.id)}
                  className="w-full flex items-center gap-3 px-2 py-2.5 rounded-xl transition-colors"
                  style={{ background: isSelected ? 'rgba(40,127,216,0.12)' : 'transparent' }}
                  onMouseEnter={e => {
                    if (!isSelected) (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.04)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLButtonElement).style.background = isSelected ? 'rgba(40,127,216,0.12)' : 'transparent';
                  }}
                >
                  {/* Avatar */}
                  <div className="relative flex-shrink-0">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-[13px]"
                      style={{ background: contact.avatarGradient }}
                    >
                      {contact.initials}
                    </div>
                    {contact.isOnline && !contact.isGroup && (
                      <span
                        className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2"
                        style={{ background: '#22C55E', borderColor: '#1A2B3C' }}
                      />
                    )}
                    {contact.isGroup && (
                      <span
                        className="absolute bottom-0 right-0 w-4 h-4 rounded-full flex items-center justify-center border-2"
                        style={{ background: '#287FD8', borderColor: '#1A2B3C' }}
                      >
                        <Users size={8} className="text-white" />
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-white font-medium text-[14px] truncate">{contact.name}</p>
                    <p className="text-[12px] truncate" style={{ color: 'rgba(255,255,255,0.45)' }}>{contact.subtitle}</p>
                  </div>

                  {/* Checkbox */}
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all"
                    style={{
                      background: isSelected ? '#287FD8' : 'transparent',
                      border: isSelected ? '2px solid #287FD8' : '2px solid rgba(255,255,255,0.25)',
                    }}
                  >
                    {isSelected && <Check size={11} className="text-white" strokeWidth={3} />}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Send button */}
        <div className="px-5 py-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          {sent ? (
            <div className="flex items-center justify-center gap-2 py-3">
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center"
                style={{ background: '#22C55E' }}
              >
                <Check size={13} className="text-white" strokeWidth={3} />
              </div>
              <span className="text-white font-medium text-[14px]">
                {mode === 'forward' ? 'Forwarded!' : 'Shared!'}
              </span>
            </div>
          ) : (
            <button
              onClick={handleSend}
              disabled={selected.size === 0}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-[14px] transition-all"
              style={{
                background: selected.size > 0 ? 'linear-gradient(135deg, #287FD8 0%, #1A5FA8 100%)' : 'rgba(255,255,255,0.07)',
                color: selected.size > 0 ? '#fff' : 'rgba(255,255,255,0.3)',
                cursor: selected.size > 0 ? 'pointer' : 'not-allowed',
              }}
            >
              <Send size={15} />
              {mode === 'forward' ? 'Forward' : 'Share'}
              {selected.size > 0 && ` to ${selected.size}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
